document.addEventListener('DOMContentLoaded', () => {
    const observedLinks = new WeakSet();

    function hasHiddenParent(element) {
        let current = element;
        while (current && current !== document.body) {
            if (
                current.getAttribute &&
                current.getAttribute('aria-hidden') === 'true'
            ) {
                return true;
            }

            const style = window.getComputedStyle(current);
            if (style.display === 'none' || style.visibility === 'hidden')
                return true;
            if (parseFloat(style.opacity) === 0) return true;

            current = current.parentElement;
        }

        return false;
    }

    function passesSliderVisibilityCheck(element) {
        const swiperSlide = element.closest('.swiper-slide');
        if (swiperSlide) {
            const classList = swiperSlide.classList;
            const inView =
                classList.contains('swiper-slide-visible') ||
                classList.contains('swiper-slide-active');

            if (!inView) return false;

            if (
                classList.contains('swiper-slide-duplicate') &&
                !classList.contains('swiper-slide-visible')
            ) {
                return false;
            }
        }

        const splideSlide = element.closest('.splide__slide');
        if (splideSlide) {
            const classList = splideSlide.classList;
            if (
                !classList.contains('is-active') &&
                !classList.contains('is-visible')
            ) {
                return false;
            }
        }

        return true;
    }

    function isElementVisible(element, entry) {
        if (hasHiddenParent(element)) return false;
        if (!passesSliderVisibilityCheck(element)) return false;

        const style = window.getComputedStyle(element);
        const positionFixed = style.position === 'fixed';

        const hasOffsetParent = element.offsetParent !== null || positionFixed;
        if (!hasOffsetParent) return false;

        const rect = element.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return false;

        const viewportWidth =
            window.innerWidth || document.documentElement.clientWidth;
        const viewportHeight =
            window.innerHeight || document.documentElement.clientHeight;

        const isInViewport =
            rect.bottom > 0 &&
            rect.right > 0 &&
            rect.top < viewportHeight &&
            rect.left < viewportWidth;

        if (!isInViewport) return false;

        if (entry) {
            const intersection = entry.intersectionRect || rect;
            const intersectionArea = intersection.width * intersection.height;
            const elementArea = rect.width * rect.height;

            if (intersectionArea === 0) return false;

            const coverage = intersectionArea / elementArea;
            if (coverage < 0.5 && !positionFixed) return false;
        }

        return true;
    }

    // Definicje typów linków z selektorami na podstawie struktury projektu
    const linkTypes = {
        banner: '.banner a, .banner-fullwidth a, .boxes a, .hero__slideTitle a, .hero__slideLink a',
        banner_lok: '.locations-banner a',
        image_link:
            '.page-article__thumbnailImg a, .card-article__image a, .event-card a img, .hero__slideTeaser a',
        in_text:
            '.page-article__blocks a, .typo-huge-cc a, .content a, .page-article__excerpt a',
        promo_links:
            '.article-list a, .page-article__related a, .page-article__articlePrev a, .page-article__articleNext a, .page-article__shareWrapper a',
        header_icon:
            '.header__box-links, .header__menu-ebilet, .mobile-fixed-menu__link, .header__menu a, .header__results-mobile a',
        header_link:
            '.header__recommendedCities a, .header__important-link, .header__recommendedPosts a',
        footer_link: '.footer a',
    };

    function registerLinkView(link, entry) {
        if (observedLinks.has(link)) return;
        if (!isElementVisible(link, entry)) return;

        observedLinks.add(link);
        trackLinkView(link);
    }

    // Observer dla widoczności linków
    const linkObserver = new IntersectionObserver(
        entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                    registerLinkView(entry.target, entry);
                }
            });
        },
        { threshold: 0.5 }
    );

    // Funkcja do określenia typu linku
    function getLinkType(element) {
        // Sprawdź data-link-type attribute - PRIORYTET
        if (element.dataset.linkType) {
            return element.dataset.linkType;
        }

        // Sprawdź czy element pasuje do selektorów
        for (const [type, selector] of Object.entries(linkTypes)) {
            if (element.matches(selector) || element.closest(selector)) {
                return type;
            }
        }

        // Sprawdź kontekst rodzica dla lepszego dopasowania
        const parent = element.closest(
            '.page-article__shareWrapper, .page-article__userMedia, .hero__slide, .header__menu, .header__recommendedCities, .header__important-links, .header__recommendedPosts, .header__results-mobile'
        );
        if (parent) {
            if (parent.classList.contains('page-article__shareWrapper')) {
                return 'promo_links';
            }
            if (parent.classList.contains('page-article__userMedia')) {
                return 'header_icon';
            }
            if (parent.classList.contains('hero__slide')) {
                // Sprawdź czy to link na obrazku w hero
                if (element.closest('.hero__slideTeaser')) {
                    return 'image_link';
                }
                // Pozostałe linki w hero to banner
                return 'banner';
            }
            if (parent.classList.contains('header__menu')) {
                return 'header_icon';
            }
            if (parent.classList.contains('header__recommendedCities')) {
                return 'header_link';
            }
            if (parent.classList.contains('header__important-links')) {
                return 'header_link';
            }
            if (parent.classList.contains('header__recommendedPosts')) {
                return 'header_link';
            }
            if (parent.classList.contains('header__results-mobile')) {
                return 'header_icon';
            }
        }

        return 'other';
    }

    // Funkcja do pobierania CTA linku
    function getLinkCta(element) {
        // Data attribute - PRIORYTET
        if (element.dataset.linkCta) {
            return element.dataset.linkCta;
        }

        // Tekst linku
        const textContent = element.textContent?.trim();
        if (textContent) return textContent;

        // Alt obrazka wewnątrz linku
        const img = element.querySelector('img');
        if (img?.alt) return img.alt;

        // Title linku
        if (element.title) return element.title;

        // Aria-label
        if (element.getAttribute('aria-label')) {
            return element.getAttribute('aria-label');
        }

        // Sprawdź ikony social media
        const iconElement = element.querySelector('.icon, [class*="icon-"]');
        if (iconElement) {
            const iconClass = Array.from(iconElement.classList).find(cls =>
                cls.startsWith('icon-')
            );
            if (iconClass) {
                return iconClass.replace('icon-', '');
            }
        }

        return '';
    }

    // Event link_view
    function trackLinkView(linkElement) {
        const linkUrl =
            linkElement.href || linkElement.getAttribute('href') || '';
        const linkType = getLinkType(linkElement);
        const linkCta = getLinkCta(linkElement);

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'link_view',
            link_url: linkUrl,
            link_type: linkType,
            link_cta: linkCta,
        });
    }

    // Event link_click
    function trackLinkClick(linkElement) {
        const linkUrl =
            linkElement.href || linkElement.getAttribute('href') || '';
        const linkType = getLinkType(linkElement);
        const linkCta = getLinkCta(linkElement);

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'link_click',
            link_url: linkUrl,
            link_type: linkType,
            link_cta: linkCta,
        });
    }

    function trackLinksInsideContainer(container) {
        if (!container) return;

        const links = container.querySelectorAll('a[href]');
        links.forEach(link => {
            registerLinkView(link);
        });
    }

    function initSliderVisibilityObservers() {
        const swiperContainers = document.querySelectorAll('.swiper');
        swiperContainers.forEach(container => {
            const slideObserver = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    if (
                        mutation.type === 'attributes' &&
                        mutation.attributeName === 'class'
                    ) {
                        const slide = mutation.target;
                        if (!slide.classList.contains('swiper-slide')) return;

                        if (
                            slide.classList.contains('swiper-slide-visible') ||
                            slide.classList.contains('swiper-slide-active')
                        ) {
                            trackLinksInsideContainer(slide);
                        }
                    }
                });
            });

            container.querySelectorAll('.swiper-slide').forEach(slide => {
                slideObserver.observe(slide, {
                    attributes: true,
                    attributeFilter: ['class'],
                });

                if (
                    slide.classList.contains('swiper-slide-visible') ||
                    slide.classList.contains('swiper-slide-active')
                ) {
                    trackLinksInsideContainer(slide);
                }
            });
        });

        const splideContainers = document.querySelectorAll('.splide');
        splideContainers.forEach(container => {
            const slideObserver = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    if (
                        mutation.type === 'attributes' &&
                        mutation.attributeName === 'class'
                    ) {
                        const slide = mutation.target;
                        if (!slide.classList.contains('splide__slide')) return;

                        if (
                            slide.classList.contains('is-active') ||
                            slide.classList.contains('is-visible')
                        ) {
                            trackLinksInsideContainer(slide);
                        }
                    }
                });
            });

            container.querySelectorAll('.splide__slide').forEach(slide => {
                slideObserver.observe(slide, {
                    attributes: true,
                    attributeFilter: ['class'],
                });

                if (
                    slide.classList.contains('is-active') ||
                    slide.classList.contains('is-visible')
                ) {
                    trackLinksInsideContainer(slide);
                }
            });
        });
    }

    // Inicjalizacja trackingu dla wszystkich linków
    function initLinkTracking() {
        const allLinks = document.querySelectorAll('a[href]');

        allLinks.forEach(link => {
            // Dodaj observer dla widoczności
            linkObserver.observe(link);

            // Dodaj listener dla kliknięć z przekazaniem event
            link.addEventListener('click', () => {
                trackLinkClick(link);
            });
        });
    }

    // Funkcja do trackingu nowych linków (dla dynamicznie ładowanej zawartości)
    function observeNewLinks() {
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // Sprawdź czy dodany element to link
                        if (node.tagName === 'A' && node.href) {
                            linkObserver.observe(node);
                            node.addEventListener('click', () => {
                                trackLinkClick(node);
                            });
                        }

                        // Sprawdź linki wewnątrz dodanego elementu
                        const newLinks = node.querySelectorAll('a[href]');
                        newLinks.forEach(link => {
                            linkObserver.observe(link);
                            link.addEventListener('click', () => {
                                trackLinkClick(link);
                            });
                        });
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    // Inicjalizacja
    initLinkTracking();
    observeNewLinks();
    initSliderVisibilityObservers();

    // Funkcja eksportowana do użycia w innych skryptach
    window.initLinkTrackingForContainer = function (container) {
        if (!container) return;

        const links = container.querySelectorAll('a[href]');
        links.forEach(link => {
            linkObserver.observe(link);
            link.addEventListener('click', () => {
                trackLinkClick(link);
            });
            registerLinkView(link);
        });
    };
});
